import React, { useState, useEffect, useImperativeHandle, useRef } from 'react';
import { Calendar, FileDown, Plus, Snowflake, Trash2 } from 'lucide-react';
import { generateTEPDF } from '../../../../utils/workflowPDF';
import { useTESession } from '../../../../hooks/useTESession';
import {
  EMPTY_TE_SESSION,
  CL_QUALITY,
  RECIPIENT_STATUS,
  calculatePostTEDates,
  EMBRYO_STAGE_LABELS,
  generateTempId
} from '../../../../constants/workflowGranular';
import { MARK } from '../../../../constants';
import { ehDoadora, ehTouro, ehReceptora } from '../../../../utils/animais';
import { calcularEntrada, taxaDeEntrada } from '../../../../utils/calculations/entrada';
import { fetchEmbryosForTe, insertManualEmbryos, newEmbryoId, embriaoDisponivelPara, embrioesDaTE, saldoDeEmbrioes, salvarNumeracao, numerosRepetidos } from '../../../../data/embryos';
import EquipeDaEtapa, { equipeNoWorkflow } from '../../shared/EquipeDaEtapa';
import ImportarDaEtapa from '../../shared/ImportarDaEtapa';
import DescongeladosDoEstoque from '../../shared/DescongeladosDoEstoque';
import { usosForaDoPrazo, textoDoDescongelado, limiteDoDescongelado, hojeLocal, previaDaDesvitrificacao } from '../../../../data/descongelados';
import { diagnosticosComResultado } from '../../../../data/dg';
import BuscaDeAnimal from '../../shared/BuscaDeAnimal';
import { embrioesQueFaltam, embrioesSincronizados } from '../../../../utils/importarDaEtapa';
import { descreverPerda, confirmarRemocao } from '../../../../utils/confirmarRemocao';
import { enderecoDoBotijao } from '../../../../data/estoque';
import { isRetryable } from '../../../../data/errors';
import { sessaoDoLocal } from '../../../../data/clients';

const TETab = React.forwardRef(({ service, workflowData, onUpdate, laboratoryId, laboratory,
                                 entradaEmitida = null, clients = [], isActive = true }, ref) => {
  const { loadTEData, saveTEData } = useTESession(laboratoryId);

  const [teData, setTeData]           = useState(EMPTY_TE_SESSION);
  const [isInitialized, setIsInitialized] = useState(false);
  const equipe = equipeNoWorkflow(workflowData, onUpdate, 'te');
  const [availableEmbryos, setAvailableEmbryos] = useState([]);
  const [loadingEmbryos, setLoadingEmbryos]     = useState(false);
  const [transfers, setTransfers]     = useState({});
  const [manualEmbryos, setManualEmbryos] = useState([]);  // embriões adicionados manualmente
  const [manualQuantity, setManualQuantity] = useState(service?.te_quantity || 0);
  const [escolhendoDoEstoque, setEscolhendoDoEstoque] = useState(false);
  // Em estado local, não lida do serviço a cada render: lendo de lá, qualquer
  // re-render desfaria a edição em curso e a taxa voltaria sozinha ao padrão.
  const [taxaEntrada, setTaxaEntrada] = useState(() => taxaDeEntrada(service, laboratory));

  // A etapa JA chegou concluida, do banco ou da fila offline?
  //
  // Guarda a resposta do carregamento porque a trava do tecnico so vale para
  // quem CONCLUI agora. Etapa que ja veio concluida e sem tecnico e' dado a
  // consertar, nao acao a barrar — e barrar travava o salvamento de todas as
  // outras abas junto.
  const concluidaAoCarregar = useRef(false);

  // Os embriões cujo número foi digitado NESTA aba e ainda não foi gravado.
  //
  // É o que a TE tem direito de escrever na numeração. Antes ela regravava a
  // lista inteira, e a lista inteira é uma cópia do banco de quando o serviço
  // abriu: numerar na FIV e salvar devolvia a numeração velha por cima, no
  // mesmo clique. O que não foi digitado aqui não é opinião desta aba.
  const numerosEditados = useRef(new Set());

  // O número que o embrião tinha no banco antes de alguém digitar por cima.
  // É a reserva para quando a gravação da numeração não sobe: a TE precisa ir
  // com um número que exista do outro lado, e esse é o anterior.
  const numerosOriginais = useRef(new Map());

  // ── Carregar embriões do DB ────────────────────────────────────────────────
  const loadEmbryos = async () => {
    if (!service?.id || !laboratoryId) return;
    setLoadingEmbryos(true);
    try {
      // Carrega fresh (disponíveis) + transferred (já transferidos) — exclui discard/frozen-sem-DT
      setAvailableEmbryos(await fetchEmbryosForTe(service, laboratoryId) || []);
    } catch (err) {
      console.error('Erro ao carregar embrioes:', err);
      setAvailableEmbryos([]);
    } finally {
      setLoadingEmbryos(false);
    }
  };

  // ── Inicialização ──────────────────────────────────────────────────────────
  useEffect(() => {
    if (isInitialized || !laboratoryId || !service?.id) return;

    const initializeData = async () => {
      await loadEmbryos();

      const normalized = await loadTEData(service?.id);
      const source = normalized || service?.te_data;

      if (source) {
        setTeData(source);
        concluidaAoCarregar.current = Boolean(source.completed)
          && !source.session?.technician_id && !source.session?.technician_name;
        const transfersMap = {};
        (source.transfers || []).forEach(t => {
          const key = t.embryo_id || `temp-${t.embryo_number}`;
          transfersMap[key] = t;
        });
        setTransfers(transfersMap);

        // Resiliência: se há transfers salvos cujo embrião não está mais na tabela embryos
        // (ex: deletado por bug anterior), reconstrói entradas sintéticas para exibir as linhas.
        setAvailableEmbryos(prev => {
          const existingIds  = new Set(prev.map(e => e.id));
          const existingNums = new Set(prev.map(e => e.service_number));
          const synthetic = (source.transfers || [])
            .filter(t => {
              const missingById  = !t.embryo_id || !existingIds.has(t.embryo_id);
              const missingByNum = !t.embryo_number || !existingNums.has(t.embryo_number);
              return missingById && missingByNum;
            })
            .map(t => ({
              id:             t.embryo_id || `synth-${t.embryo_number}`,
              service_number: t.embryo_number || null,
              destination:    'transferred',
              donor_identifier: t.donor_name || null,
              bull_name:      t.bull || null,
              stage:          null,
              donor:          null,
              _synthetic:     true,
            }));
          return synthetic.length > 0 ? [...prev, ...synthetic] : prev;
        });
      } else {
        setTeData({
          ...EMPTY_TE_SESSION,
          session: {
            date: workflowData?.te_date || service?.te_scheduled_date || null,
            // A cidade vem do local do serviço, como no protocolo. Era
            // campo vazio aqui, e a mesma fazenda tinha o município digitado
            // de novo em cada TE — o sistema já sabia a resposta.
            ...sessaoDoLocal(service, clients),
            technician_id: null,
            technician_name: ''
          },
          completed: workflowData?.te_done || false
        });
      }
      setIsInitialized(true);
    };

    initializeData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [service?.id, laboratoryId, isInitialized]);

  // Re-indexa transfers pelo UUID atual do embrião usando service_number como chave estável
  useEffect(() => {
    if (!isInitialized || availableEmbryos.length === 0) return;
    setTransfers(prev => {
      const updated = { ...prev };
      let changed   = false;

      // Mapa service_number → UUID atual
      const byNumber = {};
      // O descongelado do estoque tem número de outro serviço: fora do mapa,
      // senão roubaria a transferência do embrião daqui de mesmo número.
      availableEmbryos.filter(e => !e._doEstoque)
        .forEach(e => { byNumber[e.service_number] = e.id; });

      // IDs de embriões atualmente válidos
      const validIds = new Set(availableEmbryos.map(e => e.id));

      Object.entries(updated).forEach(([key, t]) => {
        // 1. Temp-key → UUID real
        if (key.startsWith('temp-')) {
          const sn = parseInt(key.replace('temp-', ''));
          const realId = byNumber[sn];
          if (realId) {
            updated[realId] = { ...t, embryo_id: realId };
            delete updated[key];
            changed = true;
          }
          return;
        }
        // 2. UUID stale (não existe mais nos embriões atuais) → re-linka pelo embryo_number
        if (!validIds.has(key) && t.embryo_number) {
          const realId = byNumber[t.embryo_number];
          if (realId) {
            updated[realId] = { ...t, embryo_id: realId };
            delete updated[key];
            changed = true;
          }
        }
      });

      return changed ? updated : prev;
    });
  }, [availableEmbryos, isInitialized]);

  // ── Handlers sessão ────────────────────────────────────────────────────────
  const handleSessionChange = (field, value) =>
    setTeData(prev => ({ ...prev, session: { ...prev.session, [field]: value } }));

  // ── Handlers embriões manuais ──────────────────────────────────────────────
  const handleAddManualEmbryo = () => {
    const id = generateTempId();
    setManualEmbryos(prev => [...prev, { id, donor_identifier: '', bull_name: '', stage: '', tipo: 'TE' }]);
    setTransfers(prev => ({
      ...prev,
      [id]: { embryo_id: id, status: null, recipient: {}, pre_evaluation: {}, dates: {}, observations: '' }
    }));
  };

  const handleRemoveManualEmbryo = (id) => {
    // A transferencia vai junto com o embriao: receptora, avaliacao e datas
    // ficam indexadas pelo id dele. Sai sem pergunta so' quando nada foi
    // preenchido ainda.
    const emb = manualEmbryos.find(e => e.id === id);
    const t = transfers[id] || {};
    const perdas = [];
    if (t.recipient?.identifier) perdas.push(`receptora ${t.recipient.identifier}`);
    if ((t.observations || '').trim()) perdas.push('observações');

    if (!confirmarRemocao(descreverPerda({
      nome: emb?.embryo_number ? `embrião ${emb.embryo_number}` : emb?.donor_name,
      perdas,
    }))) return;

    setManualEmbryos(prev => prev.filter(e => e.id !== id));
    setTransfers(prev => { const next = { ...prev }; delete next[id]; return next; });
  };

  const handleManualEmbryoChange = (id, field, value) =>
    setManualEmbryos(prev => prev.map(e => e.id === id ? { ...e, [field]: value } : e));

  /**
   * Tirar da TE um descongelado lançado por engano (pedido de 29/10/2026: o
   * laboratório desvitrificou a haste errada e não tinha como voltar).
   *
   * Só sai da tela; ao salvar, `save_te_session` apaga a transferência e
   * devolve o embrião ao botijão do dono (o caminho de volta da 20261007).
   * O "Substituir pela FIV" fazia isso com a TE inteira e trava com entrada
   * emitida; aqui a entrada não trava — ela fica com o número antigo, e o
   * aviso manda refaturá-la no Financeiro.
   *
   * Trava com DG lançado para o serviço: o resultado está preso às
   * transferências, e apagar uma levaria junto o vínculo do diagnóstico.
   */
  const handleRemoverDescongelado = async (id) => {
    const emb = availableEmbryos.find(e => e.id === id);
    const comResultado = await diagnosticosComResultado(service.id);
    if (comResultado > 0) {
      alert(`${comResultado} diagnóstico(s) de prenhez já têm resultado para as transferências `
        + 'desta TE. Tirar um embrião daqui levaria junto o vínculo do resultado.');
      return;
    }
    const t = transfers[id] || {};
    // Outro embrião da haste com receptora: a haste foi aberta mesmo, e este
    // vira sobra. Sobra que veio de outra TE também não volta ao botijão. Só
    // volta quando nada da haste foi usado, que é a correção da haste errada.
    const haOutroUsado = Boolean(emb?.haste) && availableEmbryos.some(o =>
      o.id !== id && o.haste === emb.haste && transfers[o.id]?.status);
    const ficaDescongelado = haOutroUsado || Boolean(emb?.descongelado_em && !t.status);
    const pergunta = [
      `Tirar o embrião ${emb?.service_number != null ? `#${emb.service_number}` : 'descongelado'} desta TE?`,
      t.recipient?.identifier ? `A receptora ${t.recipient.identifier} sai junto.` : '',
      ficaDescongelado
        ? 'Ele não volta ao botijão: fica como descongelado sem uso, para outra TE em até 48 horas. '
          + 'Se foi a haste errada, tire todos os embriões dela desta TE para devolvê-la.'
        : 'Ao salvar, ele volta ao botijão (estoque do dono).',
      entradaEmitida
        ? `\nA entrada ${entradaEmitida.numero || ''} já saiu contando este embrião: depois de salvar, `
          + 'use "Refaturar entrada" no Financeiro para ela sair com o número certo.'
        : '',
    ].filter(Boolean).join('\n');
    if (!window.confirm(pergunta)) return;

    setAvailableEmbryos(prev => prev.filter(e => e.id !== id));
    setTransfers(prev => { const next = { ...prev }; delete next[id]; return next; });
  };

  /**
   * Descongelados do estoque entram como linha da TE, iguais às outras: a
   * receptora e o resultado se lançam ali, e a aprovação é o que conta na
   * cobrança. Saem do botijão ao salvar com a transferência aprovada.
   */
  const handleAdicionarDoEstoque = (escolhidos) => {
    setAvailableEmbryos(prev => {
      const ja = new Set(prev.map(e => e.id));
      return [...prev, ...escolhidos.filter(e => !ja.has(e.id))];
    });
    setTransfers(prev => {
      const next = { ...prev };
      escolhidos.forEach(e => {
        if (!next[e.id]) {
          next[e.id] = { embryo_id: e.id, status: null, recipient: {}, pre_evaluation: {}, dates: {}, observations: '' };
        }
      });
      return next;
    });
  };

  // ── Handler transferências ─────────────────────────────────────────────────
  /**
   * Número do embrião, editado na própria TE.
   *
   * Mexe só na lista carregada; a gravação acontece no save da aba, junto do
   * resto. Salvar a cada tecla mandaria uma escrita por dígito e brigaria com
   * a conferência de número repetido, que só faz sentido sobre o conjunto.
   */
  const alterarNumero = (embryoId, numero) => {
    if (!numerosOriginais.current.has(embryoId)) {
      const atual = availableEmbryos.find(e => e.id === embryoId);
      numerosOriginais.current.set(embryoId, atual?.service_number ?? null);
    }
    numerosEditados.current.add(embryoId);
    setAvailableEmbryos(anteriores => anteriores.map(e =>
      e.id === embryoId ? { ...e, service_number: numero } : e));
  };

  /**
   * Escolher a receptora na lista grava o ID, nao so o brinco.
   *
   * `te_transfers.recipient_id` existe, tem indice e e passada ao RPC desde
   * sempre -- a tela e que nunca a preenchia, porque o campo era um input de
   * texto solto. Na pratica a coluna estava NULA em toda transferencia, e a
   * receptora era tres strings digitadas em tres etapas sem nada ligando uma
   * a outra.
   */
  const handleSelectRecipient = (embryoId, animal) => {
    handleTransferChange(embryoId, 'recipient.identifier', animal.identifier);
    handleTransferChange(embryoId, 'recipient.animal_id', animal.id);
    handleTransferChange(embryoId, 'recipient.breed', animal.breed || null);
  };

  /**
   * Digitar por cima solta o vinculo.
   *
   * Sem isto, corrigir o brinco de uma receptora ja escolhida deixaria o id da
   * ANTERIOR gravado, e a transferencia apontaria para a vaca errada -- sem
   * nada na tela denunciando.
   */
  const handleTypeRecipient = (embryoId, texto) => {
    handleTransferChange(embryoId, 'recipient.identifier', texto);
    handleTransferChange(embryoId, 'recipient.animal_id', null);
  };

  const handleTransferChange = (embryoId, field, value) => {
    setTransfers(prev => {
      const transfer = prev[embryoId] || {
        embryo_id: embryoId, status: null, recipient: {}, pre_evaluation: {}, dates: {}, observations: ''
      };
      const updated = { ...transfer };
      if (field.includes('.')) {
        const [parent, child] = field.split('.');
        updated[parent] = { ...updated[parent], [child]: value };
      } else {
        updated[field] = value;
      }
      // Receptora preenchida É a transferência aprovada. Havia um check
      // verde/vermelho para isso, mas em 1702 de 1709 transferências com
      // receptora o técnico só clicava no verde: era um passo a mais que,
      // esquecido, deixava o embrião no saldo e a receptora fora da DG30.
      // Sem receptora, o embrião não foi transferido.
      if (field === 'recipient.identifier') {
        const temReceptora = !!(value || '').trim();
        updated.status = temReceptora ? RECIPIENT_STATUS.APPROVED : null;
        if (!temReceptora) {
          updated.dates = {};
        } else if (teData.session.date) {
          // A FIV manda nas DGs; a TE só entra como reserva quando não há FIV
          // registrada no serviço.
          const dataFIV = service?.lab_date || service?.lab_scheduled_date || null;
          updated.dates = calculatePostTEDates(teData.session.date, dataFIV);
        }
      }
      return { ...prev, [embryoId]: updated };
    });
  };

  // ── Sync onUpdate ──────────────────────────────────────────────────────────
  useEffect(() => {
    if (!isInitialized) return;

    const transfersArray = Object.values(transfers);
    const approvedCount  = transfersArray.filter(t => t.status === RECIPIENT_STATUS.APPROVED).length;
    const totalEmbryos   = availableEmbryos.length + manualEmbryos.length;

    const transferidos = totalEmbryos === 0 ? manualQuantity : approvedCount;

    onUpdate('te_date', teData.session.date);
    onUpdate('te_done', teData.completed);
    onUpdate('te_quantity', transferidos);

    // A entrada nasce aqui: quantidade e taxa ficam CONGELADAS no serviço.
    // A taxa é copiada do laboratório no lançamento — se o padrão mudar
    // amanhã, este lançamento continua contando a história do dia em que
    // aconteceu.
    //
    // CONGELADAS DE VERDADE a partir da emissão. O comentário acima dizia
    // isso desde sempre, mas o efeito regravava os dois a cada salvamento da
    // TE: reabrir uma TE já faturada e salvar sobrescrevia a quantidade que a
    // fatura tinha cobrado, e a conta passava a não fechar com o papel que o
    // cliente recebeu. Emitida, a fatura é o documento — a tela não opina
    // mais sobre ela.
    if (!entradaEmitida) {
      onUpdate('entrada_embrioes', transferidos);
      onUpdate('entrada_por_embriao', taxaEntrada);
    }

    if (teData.completed && approvedCount > 0) {
      const approvedTransfers = transfersArray.filter(
        t => t.status === RECIPIENT_STATUS.APPROVED && t.recipient?.identifier
      );
      const dg30Checks = approvedTransfers.map((t, index) => {
        const embryo = availableEmbryos.find(e => e.id === t.embryo_id);
        return {
          id: `dg30-${index}`,
          transfer_id: t.id,
          recipient_id: t.recipient.animal_id,
          recipient_identifier: t.recipient.identifier,
          embryo_number: embryo?.service_number || t.embryo_number,
          te_date: teData.session.date,
          expected_date: t.dates?.expected_dg30,
          result: null, viability: null, heartbeat: null, size_mm: null,
          empty_cause: null, inconclusive_reason: null, reschedule_date: null, observations: ''
        };
      });

      if (dg30Checks.length > 0) {
        onUpdate('pregnancy_data', {
          dg30: {
            session: { date: null, technician_id: null, technician_name: '', method: 'Ultrassom' },
            checks: dg30Checks, completed: false
          },
          dg60: {
            session: { date: null, technician_id: null, technician_name: '', method: 'Ultrassom' },
            checks: [], completed: false, billing_quantity: 0
          }
        });
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [teData, transfers, isInitialized, manualQuantity, availableEmbryos.length, manualEmbryos.length]);

  /**
   * Relê a FIV: traz o que falta e atualiza o que mudou.
   *
   * As abas do workflow são montadas todas de uma vez, escondidas por CSS.
   * Esta aqui lia os embriões UMA vez, na abertura do serviço, e ficava com
   * essa cópia até o serviço fechar — a numeração feita na aba de FIV logo ao
   * lado não chegava nunca.
   *
   * Por isso a releitura não é só "acrescentar o que falta", como nas outras
   * etapas: número de embrião não é coisa que se digita nas duas telas e a
   * gente escolhe uma. Ele é do embrião, e a FIV é onde ele nasce.
   *
   * O que esta aba digitou e ainda não gravou continua de pé, e a linha que a
   * FIV não tem mais continua na lista — as duas regras estão em
   * `embrioesSincronizados`, com o porquê de cada uma.
   */
  const sincronizarComAFIV = async () => {
    const daFiv = await fetchEmbryosForTe(service, laboratoryId) || [];
    const porId = new Map(daFiv.map(e => [e.id, e]));

    const trazidas = embrioesQueFaltam(availableEmbryos, daFiv).length;
    const atualizadas = availableEmbryos.filter(e => {
      const doBanco = porId.get(e.id);
      return doBanco && !numerosEditados.current.has(e.id)
        && doBanco.service_number !== e.service_number;
    }).length;

    setAvailableEmbryos(prev =>
      embrioesSincronizados(prev, daFiv, numerosEditados.current));
    return { trazidas, atualizadas };
  };

  /**
   * "Substituir pela FIV": descarta as transferências da tela (receptoras,
   * avaliação, resultado) e relista os embriões da FIV como estão agora.
   *
   * Ao salvar, `save_te_session` apaga as transferências que saíram e devolve
   * ao estoque o embrião que deixou de ter transferência aprovada (migration
   * 20261007). Sem isso o embrião ficava "transferido" para sempre.
   *
   * Bloqueia em dois casos, porque o descarte não alcança o que já saiu da TE:
   *   - fatura da entrada emitida: ela foi cobrada por ESTES transferidos;
   *   - diagnóstico com resultado: ele está preso a estas transferências.
   */
  const substituirPelaFIV = async () => {
    if (entradaEmitida) {
      return { bloqueio: `A fatura da entrada ${entradaEmitida.numero || ''} já saiu com estes `
        + 'embriões. Substituir a TE deixaria a cobrança sem as transferências.' };
    }
    const comResultado = await diagnosticosComResultado(service.id);
    if (comResultado > 0) {
      return { bloqueio: `${comResultado} diagnóstico(s) de prenhez já têm resultado `
        + 'para estas transferências.' };
    }

    const daFiv = await fetchEmbryosForTe(service, laboratoryId) || [];
    const preenchidas = Object.values(transfers)
      .filter(t => t.recipient?.identifier || t.status).length;
    const pergunta = [
      `Substituir a TE pelos ${daFiv.length} embriões da FIV?`,
      '',
      preenchidas > 0 || manualEmbryos.length > 0
        ? `Vai junto: ${[
          preenchidas > 0 && `${preenchidas} transferência(s) com receptora ou resultado`,
          manualEmbryos.length > 0 && `${manualEmbryos.length} embrião(ões) incluído(s) à mão e não salvos`,
        ].filter(Boolean).join(', ')}.`
        : 'Nenhuma receptora lançada será perdida.',
      '',
      'Os embriões transferidos voltam ao estoque ao salvar. Nada é gravado até "Salvar Workflow".',
    ].join('\n');
    if (!window.confirm(pergunta)) return null;

    numerosEditados.current.clear();
    setTransfers({});
    setManualEmbryos([]);
    setAvailableEmbryos(daFiv);
    return daFiv.length;
  };

  /**
   * Abrir a aba relê a FIV.
   *
   * O botão de importar continua existindo — para quem mudou a FIV noutro
   * aparelho, ou noutra janela, sem sair desta aba. Mas o caminho comum é
   * numerar na FIV e passar para cá, e esse não podia depender de lembrar de
   * clicar em nada: quem vai a campo lê o número que está na tela.
   */
  useEffect(() => {
    if (!isActive || !isInitialized) return;
    sincronizarComAFIV().catch(err =>
      console.error('Erro ao reler os embriões da FIV:', err));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isActive, isInitialized]);

  // ── Save ───────────────────────────────────────────────────────────────────
  useImperativeHandle(ref, () => ({
    save: async () => {
      if (!isInitialized || !service?.id) return;

      // Técnico é cobrado no ATO de concluir, e só nele.
      //
      // Duas correções na mesma regra, cada uma de um jeito de travar demais:
      //
      // 1. Antes, disparava com qualquer transferência existente — e um objeto
      //    de transferência nasce ao alterar qualquer campo, até marcar um CL.
      //    O técnico era exigido no meio do preenchimento.
      //
      // 2. Agora, só dispara na TRANSIÇÃO para concluída. Etapa que já chegou
      //    concluída e sem técnico — dado antigo, item vindo da fila offline —
      //    não pode travar o salvamento para sempre. E travava tudo, não só a
      //    TE: o botão salvar percorre TODAS as abas, então uma TE nesse
      //    estado impedia de gravar o PROTOCOLO, a OPU e a FIV, com uma
      //    mensagem sobre a TE numa tela onde a TE nem aparece.
      //
      // O nome da aba entra na mensagem pelo mesmo motivo: quem lê está
      // olhando outra etapa.
      const temTecnico = teData.session.technician_id || teData.session.technician_name;
      if (teData.completed && !temTecnico && !concluidaAoCarregar.current) {
        throw new Error('Aba TE: informe o técnico responsável antes de concluir a etapa.');
      }

      const vencidos = usosForaDoPrazo(availableEmbryos, transfers, teData.session.date || hojeLocal());
      if (vencidos.length > 0) {
        throw new Error(`Aba TE: ${vencidos.map(e => `o embrião #${e.service_number ?? '—'}`).join(', ')} `
          + `foi descongelado há mais de 48 horas nesta data (limite ${
            limiteDoDescongelado(vencidos[0].descongelado_em).split('-').reverse().join('/')}). `
          + 'Confira a data da TE ou tire a receptora dele.');
      }

      let finalTransfers = { ...transfers };

      // id do embrião → número. Começa pela lista da tela e recebe os manuais
      // logo abaixo: `sincronizarComAFIV` atualiza o ESTADO, não esta variável,
      // então sem isto o embrião lançado à mão gravava a transferência sem
      // número nenhum — e é esse número que o DG30 herda depois.
      const numberById = {};
      availableEmbryos.forEach(e => { numberById[e.id] = e.service_number; });

      // Inserir embriões manuais no DB antes de salvar a TE
      if (manualEmbryos.length > 0) {
        const maxNum = availableEmbryos.reduce((m, e) => Math.max(m, e.service_number || 0), 0);
        // Id gerado aqui: é o que permite ligar a transferência ao embrião sem
        // esperar resposta do servidor. Antes esse vínculo dependia do id que
        // vinha do banco, e offline ele nunca chegava.
        const payload = manualEmbryos.map((e, i) => ({
          id: newEmbryoId(),
          laboratory_id: laboratoryId,
          service_id: service.id,
          service_number: maxNum + i + 1,
          donor_id: e.donor_id || null,
          donor_identifier: e.donor_identifier || null,
          bull_name: e.bull_name || null,
          stage: e.stage || null,
          destination: 'fresh',
          quality: null,
        }));

        await insertManualEmbryos(payload);

        // Troca o id provisório pelo definitivo. Não depende de resposta do
        // servidor: o id já é conhecido desde a montagem do payload.
        manualEmbryos.forEach((me, i) => {
          const idFinal = payload[i].id;
          numberById[idFinal] = payload[i].service_number;
          if (finalTransfers[me.id]) {
            finalTransfers[idFinal] = { ...finalTransfers[me.id], embryo_id: idFinal };
            delete finalTransfers[me.id];
          }
        });
        setManualEmbryos([]);
        await sincronizarComAFIV();
      }

      // Só o que foi digitado NESTA aba. A lista inteira é cópia do banco de
      // quando o serviço abriu; regravá-la toda desfazia a numeração que a FIV
      // tinha acabado de corrigir, no mesmo clique do "Salvar".
      //
      // Antes do `enriched` de propósito: é a numeração que decide qual número
      // cada transferência leva, e a TE não pode ser gravada com um número que
      // não existe no banco.
      const numerados = availableEmbryos.filter(e => numerosEditados.current.has(e.id));
      let aviso = null;

      if (numerados.length > 0) {
        // Contra a lista TODA: o repetido só aparece comparando com quem não
        // foi tocado. Conferir só os digitados deixaria passar o número que já
        // era do vizinho.
        const repetidos = numerosRepetidos(availableEmbryos.filter(e => !e._doEstoque));
        if (repetidos.length > 0) {
          throw new Error(
            `Número de embrião repetido: ${repetidos.join(', ')}. `
            + 'É por ele que se acha a palheta, então dois não podem dividir o mesmo.');
        }

        try {
          await salvarNumeracao(service.id, numerados.map(e => ({
            id: e.id, service_number: e.service_number, stage: e.stage,
          })));
          numerosEditados.current = new Set();
          numerosOriginais.current = new Map();
        } catch (err) {
          /**
           * SEM SINAL, A NUMERAÇÃO NÃO SOBE — MAS A TE NÃO SE PERDE POR ISSO.
           *
           * Esta é a única escrita do workflow que não é enfileirável, e é de
           * propósito: ela relê o estado dos embriões para recalcular as
           * contagens, e reenviar às cegas gravaria por cima de uma numeração
           * feita nesse meio-tempo.
           *
           * O problema era a consequência. A exceção subia até o modal, que
           * parava o laço antes de gravar a sessão E antes do espelho no
           * serviço: nada era salvo e nada era enfileirado. Perdia-se a TE
           * inteira — receptoras, CL, aprovações — justamente na etapa que se
           * faz no curral, que é onde não há sinal.
           *
           * Agora a TE vai com a numeração ANTERIOR, que é a que existe do
           * outro lado, e a edição fica guardada para o próximo salvamento com
           * sinal. Erro que não é de rede continua subindo: permissão negada e
           * dado inválido não melhoram por tentar de novo.
           */
          if (!isRetryable(err)) throw err;

          numerados.forEach(e => {
            if (e.id in numberById) {
              numberById[e.id] = numerosOriginais.current.get(e.id) ?? null;
            }
          });
          aviso = 'A TE foi guardada, mas a numeração dos embriões não subiu por falta de '
            + 'sinal. Ela foi gravada com a numeração anterior. Abra a TE de novo com sinal '
            + 'e salve para valer os números que você digitou.';
        }
      }

      // O número vem do EMBRIÃO, não da transferência. O que está gravado em
      // `te_transfers` é uma cópia do número de quando a transferência foi
      // lançada, e ela ficava na frente: renumerar na FIV corrigia o embrião e
      // a transferência seguia mostrando o número antigo — que é o que o DG30
      // depois herda e o que vai para a receptora na ficha.
      //
      // A cópia gravada continua como reserva, para a transferência cujo
      // embrião sumiu da tabela: ali ela é o único número que restou. E no
      // caminho sem sinal o mapa já voltou para a numeração anterior.
      const enriched = Object.values(finalTransfers).map(t => ({
        ...t,
        embryo_number: numberById[t.embryo_id] ?? t.embryo_number ?? null,
      }));

      const salvo = await saveTEData(service.id, { ...teData, transfers: enriched });
      if (salvo === null) return null;
      return aviso ? { aviso } : salvo;
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }), [isInitialized, teData, transfers, manualEmbryos, availableEmbryos, service?.id]);

  // ── Stats ──────────────────────────────────────────────────────────────────
  // O que já saiu noutro serviço não conta como embrião DESTE: a linha aparece
  // para dizer onde foi parar, mas somá-la aqui inflaria o total e o saldo.
  const embrioesDisponiveis = availableEmbryos.filter(e => embriaoDisponivelPara(e, service?.id));
  // O descongelado não é produção deste lote: somá-lo inflaria o "produzido".
  const balanco = saldoDeEmbrioes(availableEmbryos.filter(e => !e._doEstoque));

  const totalEmbryos = embrioesDisponiveis.length + manualEmbryos.length;
  const stats = {
    total:         totalEmbryos,
    withRecipient: Object.values(transfers).filter(t => t.recipient?.identifier).length,
    approved:      Object.values(transfers).filter(t => t.status === RECIPIENT_STATUS.APPROVED).length,
  };

  // Helper: nome + RGD da doadora
  const donorLabel = (embryo) => {
    const name = embryo.donor?.identifier || embryo.donor_identifier || '—';
    const rgd  = embryo.donor?.rgd;
    return rgd ? `${name} | ${rgd}` : name;
  };

  // ── Render de uma linha de embrião ─────────────────────────────────────────
  const renderEmbryoRow = (embryo, embryoKey, isManual = false, manualIndex = null) => {
    const transfer      = transfers[embryoKey] || {};
    const hasCL         = transfer.pre_evaluation?.corpus_luteum;
    const isTransferred = !isManual && embryo.destination === 'transferred';
    // Congelado precisa ser DESCONGELADO antes de ir para a receptora. Ele
    // passou a aparecer nesta lista (antes sumia), e aparecer sem aviso seria
    // trocar um problema por outro: o técnico levaria a palheta errada.
    const isCongelado = !isManual && embryo.destination === 'frozen';
    const usadoNoutro   = !isManual && !embriaoDisponivelPara(embryo, service?.id);

    // Embrião que já saiu noutro serviço não volta a ser oferecido — seria a
    // porta para transferir o mesmo duas vezes. Mas continua na lista, porque
    // some-lo faria parecer que o embrião nunca existiu; o que ele mostra é
    // onde foi parar.
    if (usadoNoutro) {
      return (
        <tr key={embryoKey} className="bg-slate-50 text-slate-400">
          <td className="px-2 py-2">
            <span className="font-bold text-sm">#{embryo.service_number ?? '—'}</span>
          </td>
          <td className="px-2 py-2 whitespace-nowrap">{donorLabel(embryo)}</td>
          <td className="px-2 py-2 whitespace-nowrap">{embryo.bull_name || '—'}</td>
          <td className="px-2 py-2 whitespace-nowrap">{embryo.stage || '—'}</td>
          <td className="px-2 py-2" colSpan={6}>
            Usado no serviço{' '}
            <span className="font-mono text-slate-600">
              #{embryo.usado_service_code || '—'}
            </span>
            {embryo.usado_em && (
              <span> em {new Date(embryo.usado_em + 'T12:00:00').toLocaleDateString('pt-BR')}</span>
            )}
          </td>
        </tr>
      );
    }

    return (
      <tr key={embryoKey}
        className={`${isTransferred ? 'bg-slate-50 opacity-75' : 'hover:bg-slate-50'}`}>

        {/* N° + Tipo */}
        <td className="px-2 py-2">
          <div className="flex flex-col items-start gap-0.5">
            {isManual
              ? <span className="font-bold text-slate-400 text-sm">+</span>
              : embryo._doEstoque ? (
                /* Número de outro serviço: mostrado para achar a palheta, não
                   editado aqui — renumerar seria mexer no lote de lá. */
                <span className="font-bold text-slate-700 text-sm"
                  title={embryo.servico_numero || embryo.usado_service_code ? 'Número do serviço de origem' : 'Número da etiqueta'}>
                  #{embryo.service_number ?? '—'}
                </span>
              ) : (
                /* Editável aqui também: a numeração vem da FIV, mas quem está
                   com a palheta na mão é quem confere o número. Gravar só ao
                   salvar a TE, junto do resto — não a cada tecla. */
                <div className="flex items-center gap-0.5">
                  <span className="font-bold text-blue-600 text-sm">#</span>
                  <input
                    type="number" min="1"
                    className="w-12 px-1 py-0.5 border border-slate-200 rounded text-xs text-center font-bold text-blue-600 focus:outline-none focus:ring-1 focus:ring-blue-400"
                    value={embryo.service_number ?? ''}
                    onChange={(e) => alterarNumero(embryo.id,
                      e.target.value === '' ? null : parseInt(e.target.value))}
                  />
                </div>
              )
            }
            {isTransferred && (
              <span className="text-xs px-1 py-0.5 rounded bg-slate-200 text-slate-700 font-medium whitespace-nowrap">
                {MARK.OK} Transferido
              </span>
            )}
            {isCongelado && embryo.descongelado_em && (
              <span className="text-xs px-1 py-0.5 rounded border border-slate-300 text-slate-700 whitespace-nowrap">
                {usosForaDoPrazo([embryo], { [embryo.id]: { status: RECIPIENT_STATUS.APPROVED } }, teData.session.date || hojeLocal()).length > 0
                  ? `${MARK.WARN} `
                  : ''}
                {textoDoDescongelado(embryo.descongelado_em)}
              </span>
            )}
            {isCongelado && !embryo.descongelado_em && (
              <span
                className="text-xs px-1 py-0.5 rounded border border-slate-300 text-slate-700 whitespace-nowrap"
                title={[
                  embryo.freeze_method || 'Congelado',
                  enderecoDoBotijao(embryo) || embryo.freeze_location || '',
                ].filter(Boolean).join(' · ')}
              >
                ❄ {embryo.freeze_method === 'Direct Transfer' ? 'DT' : 'Vitri'}
              </span>
            )}
            {isManual ? (
              <select
                className="text-xs border border-slate-200 rounded px-1 py-0.5 focus:outline-none focus:ring-1 focus:ring-blue-400"
                value={embryo.tipo || 'TE'}
                onChange={(e) => handleManualEmbryoChange(embryo.id, 'tipo', e.target.value)}
              >
                <option value="TE">TE</option>
                <option value="DT">DT</option>
                <option value="Vitri">Vitri</option>
              </select>
            ) : (
              <span className="text-xs px-1 py-0.5 rounded font-medium bg-slate-100 text-slate-600">TE</span>
            )}
            {embryo._doEstoque && (
              <span className="text-xs text-slate-500 whitespace-nowrap">
                descongelado{embryo.servico_numero ? ` · de #${embryo.servico_numero}` : ''}
                {embryo.client_id && service?.client_id && embryo.client_id !== service.client_id
                  && embryo.client_name ? ` · ${embryo.client_name}` : ''}
              </span>
            )}
          </div>
        </td>

        {/* Doadora */}
        <td className="px-2 py-2 relative" style={{ minWidth: '130px' }}>
          {isManual ? (
            <BuscaDeAnimal
              laboratoryId={laboratoryId}
              valor={embryo.donor_identifier}
              onDigitar={(t) => {
                handleManualEmbryoChange(embryo.id, 'donor_identifier', t);
                handleManualEmbryoChange(embryo.id, 'donor_id', null);
              }}
              onEscolher={(animal) => {
                handleManualEmbryoChange(embryo.id, 'donor_id', animal.id);
                handleManualEmbryoChange(embryo.id, 'donor_identifier', animal.identifier);
              }}
              filtro={ehDoadora}
              categoriaPadrao="Doadora"
              clients={clients}
              clientIdPadrao={service?.client_id}
              placeholder="Buscar doadora..."
            />
          ) : (
            <span className="font-medium text-slate-800">{donorLabel(embryo)}</span>
          )}
        </td>

        {/* Touro */}
        <td className="px-2 py-2 relative" style={{ minWidth: '120px' }}>
          {isManual ? (
            <BuscaDeAnimal
              laboratoryId={laboratoryId}
              valor={embryo.bull_name}
              onDigitar={(t) => handleManualEmbryoChange(embryo.id, 'bull_name', t)}
              onEscolher={(touro) => handleManualEmbryoChange(embryo.id, 'bull_name', touro.identifier)}
              filtro={ehTouro}
              categoriaPadrao="Reprodutor"
              clients={clients}
              clientIdPadrao={service?.client_id}
              placeholder="Buscar touro..."
            />
          ) : (
            <span className="text-slate-500">{embryo.bull_name || '—'}</span>
          )}
        </td>

        {/* Estágio + badge TE/DT */}
        <td className="px-2 py-2 whitespace-nowrap">
          {isManual ? (
            <select
              className="w-full px-1 py-1 border border-slate-300 rounded text-xs focus:outline-none focus:ring-1 focus:ring-blue-500"
              value={embryo.stage || ''}
              onChange={(e) => handleManualEmbryoChange(embryo.id, 'stage', e.target.value)}
            >
              <option value="">—</option>
              {['BI','BL','BX','BN','BX-ECLO'].map(s => (
                <option key={s} value={s}>{s}</option>
              ))}
            </select>
          ) : (
            <span className="font-medium">{EMBRYO_STAGE_LABELS[embryo.stage] || embryo.stage || '—'}</span>
          )}
        </td>

        {/* Receptora */}
        <td className="px-2 py-2 relative" style={{ minWidth: '140px' }}>
          <BuscaDeAnimal
            laboratoryId={laboratoryId}
            valor={transfer.recipient?.identifier}
            onDigitar={(t) => handleTypeRecipient(embryoKey, t)}
            onEscolher={(animal) => handleSelectRecipient(embryoKey, animal)}
            filtro={ehReceptora}
            categoriaPadrao="Receptora"
            clients={clients}
            clientIdPadrao={service?.client_id}
            placeholder="Receptora..."
          />
        </td>

        {/* CL */}
        <td className="px-2 py-2 text-center">
          <input type="checkbox"
            checked={hasCL || false}
            onChange={(e) => handleTransferChange(embryoKey, 'pre_evaluation.corpus_luteum', e.target.checked)}
            className="form-checkbox h-4 w-4 text-blue-600"
          />
        </td>

        {/* Grau CL */}
        <td className="px-2 py-2" style={{ minWidth: '80px' }}>
          {hasCL ? (
            <select
              className="w-full px-1 py-0.5 border border-slate-300 rounded text-xs focus:outline-none focus:ring-1 focus:ring-blue-500"
              value={transfer.pre_evaluation?.cl_quality || ''}
              onChange={(e) => handleTransferChange(embryoKey, 'pre_evaluation.cl_quality', parseInt(e.target.value))}
            >
              <option value="">-</option>
              {CL_QUALITY.map(opt => (
                <option key={opt.value} value={opt.value}>{opt.label}</option>
              ))}
            </select>
          ) : <span className="text-slate-300 px-1">—</span>}
        </td>

        {/* DG30 previsto */}
        <td className="px-2 py-2 whitespace-nowrap">
          {transfer.dates?.expected_dg30 ? (
            <span className="text-blue-700 font-medium">
              {new Date(transfer.dates.expected_dg30).toLocaleDateString('pt-BR')}
            </span>
          ) : <span className="text-slate-300">—</span>}
        </td>

        {/* Obs */}
        <td className="px-2 py-2" style={{ minWidth: '110px' }}>
          <input type="text" placeholder="Obs..."
            className="w-full px-2 py-0.5 border border-slate-300 rounded text-xs focus:outline-none focus:ring-1 focus:ring-blue-500"
            value={transfer.observations || ''}
            onChange={(e) => handleTransferChange(embryoKey, 'observations', e.target.value)}
          />
        </td>

        {/* Remover: o incluído à mão e o descongelado do estoque */}
        <td className="px-1 py-2 text-center">
          {isManual && (
            <button onClick={() => handleRemoveManualEmbryo(embryo.id)}
              className="text-slate-300 hover:text-red-600 transition-colors">
              <Trash2 className="w-3.5 h-3.5" />
            </button>
          )}
          {!isManual && embryo._doEstoque && (
            <button onClick={() => handleRemoverDescongelado(embryo.id)}
              title="Tirar da TE e devolver ao botijão"
              className="text-slate-300 hover:text-red-600 transition-colors">
              <Trash2 className="w-3.5 h-3.5" />
            </button>
          )}
        </td>
      </tr>
    );
  };

  return (
    <div className="p-4">
      {/* Cabeçalho da sessão */}
      <div className="mb-4 p-3 bg-slate-50 border border-slate-200 rounded-lg">
        <div className="flex items-center justify-between mb-3">
          <div className="flex items-center gap-2">
            <Calendar className="w-4 h-4 text-blue-600" />
            <h4 className="font-semibold text-slate-900 text-sm">Sessão de Transferência de Embriões</h4>
          </div>
        </div>
        <div className="grid grid-cols-2 lg:grid-cols-3 gap-3">
          <div>
            <label className="block text-xs font-medium text-slate-700 mb-1">Data *</label>
            <input type="date"
              className="w-full px-2 py-1.5 border border-slate-300 rounded text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
              value={teData.session.date || ''}
              onChange={(e) => handleSessionChange('date', e.target.value)}
            />
          </div>
          <div>
            <label className="block text-xs font-medium text-slate-700 mb-1">Fazenda</label>
            <input type="text" placeholder="Nome da fazenda"
              className="w-full px-2 py-1.5 border border-slate-300 rounded text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
              value={teData.session.farm || ''}
              onChange={(e) => handleSessionChange('farm', e.target.value)}
            />
          </div>
          <div>
            <label className="block text-xs font-medium text-slate-700 mb-1">Cidade</label>
            <input type="text" placeholder="Cidade"
              className="w-full px-2 py-1.5 border border-slate-300 rounded text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
              value={teData.session.city || ''}
              onChange={(e) => handleSessionChange('city', e.target.value)}
            />
          </div>
          <div>
            <label className="block text-xs font-medium text-slate-700 mb-1">
              Entrada por embrião (R$)
            </label>
            <input type="number" min="0" step="0.01"
              className="w-full px-2 py-1.5 border border-slate-300 rounded text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
              value={taxaEntrada}
              onChange={(e) => setTaxaEntrada(e.target.value === '' ? 0 : parseFloat(e.target.value))}
            />
            {/* A conta feita, não só a taxa: quem lança precisa ver quanto a
                entrada deste serviço vai dar antes de sair da tela. */}
            {(() => {
              const entrada = calcularEntrada(
                { ...service, entrada_embrioes: workflowData?.te_quantity ?? service?.te_quantity,
                  entrada_por_embriao: taxaEntrada },
                laboratory,
              );
              if (entrada.quantidade <= 0) return null;
              return (
                <p className="text-xs text-slate-500 mt-1">
                  {entrada.quantidade} × R$ {entrada.taxa.toLocaleString('pt-BR', { minimumFractionDigits: 2 })}
                  {' = '}
                  <strong className="text-slate-800">
                    R$ {entrada.valor.toLocaleString('pt-BR', { minimumFractionDigits: 2 })}
                  </strong>
                  {/* Onde a cobrança está, na tela em que ela é decidida.
                      Antes isto só existia no Financeiro: quem lançava a TE
                      não tinha como saber se a faturinha já tinha saído, e o
                      recurso passou meses sem nenhuma emissão. */}
                  {entradaEmitida ? (
                    <span className="block text-slate-500 mt-0.5">
                      Fatura {entradaEmitida.numero || ''} já emitida
                      {entradaEmitida.em
                        && ` em ${new Date(entradaEmitida.em + 'T00:00:00').toLocaleDateString('pt-BR')}`}
                    </span>
                  ) : entrada.taxa > 0 ? (
                    <span className="block text-slate-500 mt-0.5">
                      Ao salvar, o sistema pergunta se emite a fatura desta entrada.
                    </span>
                  ) : (
                    <span className="block text-slate-500 mt-0.5">
                      Taxa zerada — este serviço fica sem entrada.
                    </span>
                  )}
                </p>
              );
            })()}
          </div>
          <EquipeDaEtapa
            laboratoryId={laboratoryId}
            responsavel={(teData.session.technician_id || teData.session.technician_name
              ? { id: teData.session.technician_id || null, nome: teData.session.technician_name }
              : null)}
            planejado={isInitialized ? equipe.planejado : null}
            ajudante={equipe.ajudante}
            onResponsavel={(id, nome) => {
              handleSessionChange('technician_id', id);
              handleSessionChange('technician_name', nome);
              equipe.responsavelNaEquipe(id, nome);
            }}
            onAjudante={equipe.ajudanteNaEquipe}
          />
        </div>
      </div>

      {/* Estatísticas */}
      <div className="mb-4 p-3 bg-white border border-slate-200 rounded-lg">
        <div className="grid grid-cols-3 gap-3 text-xs">
          <div><p className="text-slate-500">Embriões</p><p className="font-semibold text-slate-900 text-sm">{stats.total}</p></div>
          <div><p className="text-slate-500">Com receptora</p><p className="font-semibold text-blue-600 text-sm">{stats.withRecipient}</p></div>
          {/* Saldo do lote: o que a FIV produziu e ainda não foi usado nem
              descartado. É o que o laboratório ainda tem em mãos. */}
          <div title={`${balanco.produzidos} produzidos · ${balanco.transferidos} usados · ${balanco.descartados} descartados`}>
            <p className="text-slate-500">Saldo do lote</p>
            <p className="font-semibold text-slate-900 text-sm">{balanco.saldo}</p>
          </div>
        </div>
      </div>

      {/* Tabela de embriões */}
      <div className="mb-4">
        <div className="flex items-center justify-between mb-2">
          <h4 className="font-semibold text-slate-900 text-sm">Embriões para Transferência</h4>
          <div className="flex items-center gap-2">
            <ImportarDaEtapa
              rotulo="Importar da FIV"
              fonte="da FIV"
              singular="embrião trazido"
              plural="embriões trazidos"
              onImportar={sincronizarComAFIV}
              onSubstituir={substituirPelaFIV}
              rotuloSubstituir="Substituir pela FIV"
            />
            {totalEmbryos > 0 && (
              <button
                onClick={() => generateTEPDF(service, teData.session, embrioesDaTE(availableEmbryos, transfers, service?.id), transfers)}
                className="flex items-center gap-1 text-xs text-slate-700 bg-white border border-slate-300 hover:bg-slate-50 px-2 py-1 rounded transition-colors"
              >
                <FileDown className="w-3.5 h-3.5" /> PDF
              </button>
            )}
          </div>
        </div>

        {loadingEmbryos ? (
          <div className="text-center py-8 text-slate-500 text-sm">Carregando embriões...</div>
        ) : (
          <>
            {(totalEmbryos > 0) ? (
              <div className="overflow-x-auto sm:overflow-visible sm:w-max sm:min-w-full rounded-lg border border-slate-200">
                <table className="w-full text-xs border-collapse">
                  <thead className="sticky top-0 z-10">
                    <tr className="bg-slate-50 border-b border-slate-200">
                      <th className="px-2 py-2 text-left font-semibold text-slate-600 whitespace-nowrap">N°</th>
                      <th className="px-2 py-2 text-left font-semibold text-slate-600 whitespace-nowrap">Doadora</th>
                      <th className="px-2 py-2 text-left font-semibold text-slate-600 whitespace-nowrap">Touro</th>
                      <th className="px-2 py-2 text-left font-semibold text-slate-600 whitespace-nowrap">Estágio</th>
                      <th className="px-2 py-2 text-left font-semibold text-slate-600 whitespace-nowrap">Receptora</th>
                      <th className="px-2 py-2 text-center font-semibold text-slate-600 whitespace-nowrap">CL</th>
                      <th className="px-2 py-2 text-center font-semibold text-slate-600 whitespace-nowrap">Grau CL</th>
                      <th className="px-2 py-2 text-left font-semibold text-slate-600 whitespace-nowrap">DG 30D Prev.</th>
                      <th className="px-2 py-2 text-left font-semibold text-slate-600 whitespace-nowrap">Obs</th>
                      <th className="px-2 py-2 w-6"></th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-slate-100 bg-white">
                    {availableEmbryos.map(embryo =>
                      renderEmbryoRow(embryo, embryo.id, false)
                    )}
                    {manualEmbryos.length > 0 && availableEmbryos.length > 0 && (
                      <tr className="bg-slate-50">
                        <td colSpan={10} className="px-2 py-1 text-xs text-blue-600 font-medium">
                          Embriões adicionados manualmente
                        </td>
                      </tr>
                    )}
                    {manualEmbryos.map((embryo, i) =>
                      renderEmbryoRow(embryo, embryo.id, true, i)
                    )}
                  </tbody>
                </table>
              </div>
            ) : (
              <div className="border-2 border-dashed border-slate-300 rounded-lg p-6 text-center text-slate-500">
                <p className="text-sm">Nenhum embrião disponível.</p>
                <p className="text-xs mt-1">Os embriões são criados na aba FIV ou podem ser adicionados manualmente abaixo.</p>
              </div>
            )}

            {/* Botão adicionar embrião */}
            <div className="mt-2 flex flex-wrap items-center gap-4">
              <button onClick={handleAddManualEmbryo}
                className="flex items-center gap-1.5 text-slate-400 hover:text-blue-600 text-xs transition-colors px-1">
                <Plus className="w-3.5 h-3.5" /> Adicionar embrião (FIV compartilhada)
              </button>
              <button onClick={() => setEscolhendoDoEstoque(true)}
                className="flex items-center gap-1.5 text-slate-400 hover:text-blue-600 text-xs transition-colors px-1">
                <Snowflake className="w-3.5 h-3.5" /> Descongelar do estoque
              </button>
            </div>
            {(service?.services_offered || []).includes('desvitrificacao') && (() => {
              const p = previaDaDesvitrificacao(
                availableEmbryos.filter(e => e._doEstoque && embriaoDisponivelPara(e, service?.id)),
                service?.preco_desvitrificacao);
              if (p.cobrados === 0 && p.semOrigem === 0) return null;
              return (
                <p className="mt-2 px-1 text-xs text-slate-600">
                  {p.cobrados > 0 && (p.preco > 0
                    ? <>Desvitrificação: {p.cobrados} de outro laboratório × R$ {p.preco.toLocaleString('pt-BR', { minimumFractionDigits: 2 })} = <strong className="text-slate-800">R$ {p.valor.toLocaleString('pt-BR', { minimumFractionDigits: 2 })}</strong>. </>
                    : <>{MARK.WARN} {p.cobrados} de outro laboratório, mas o serviço não tem valor de desvitrificação: não será cobrado. </>)}
                  {p.semOrigem > 0 && <>{MARK.WARN} {p.semOrigem} sem origem informada: não será cobrado.</>}
                </p>
              );
            })()}
            {escolhendoDoEstoque && (
              <DescongeladosDoEstoque
                service={service}
                laboratoryId={laboratoryId}
                dataDaTe={teData.session.date}
                jaNaTe={availableEmbryos.map(e => e.id)}
                onEscolher={handleAdicionarDoEstoque}
                onFechar={() => setEscolhendoDoEstoque(false)}
              />
            )}
          </>
        )}
      </div>

      {/* Quantidade manual (fallback sem embriões) */}
      {totalEmbryos === 0 && manualEmbryos.length === 0 && (
        <div className="p-4 bg-slate-50 border border-slate-200 rounded-lg mb-4">
          <label className="block text-sm font-medium text-slate-700 mb-2">
            Quantidade de Transferências (Manual)
          </label>
          <p className="text-xs text-slate-500 mb-3">
            Sem embriões detalhados. Preencha manualmente se necessário.
          </p>
          <input type="number" min="0" step="1" placeholder="Ex: 5"
            className="w-full px-4 py-2 border border-slate-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
            value={manualQuantity || ''}
            onChange={(e) => setManualQuantity(parseInt(e.target.value) || 0)}
          />
        </div>
      )}

      {/* Resumo */}
      {totalEmbryos > 0 && (
        <div className="p-3 bg-slate-50 border border-slate-200 rounded-lg mb-4">
          <p className="text-sm text-slate-700">
            <strong>{stats.approved} transferidos</strong> — DG30 prevista calculada automaticamente ao lançar a receptora
          </p>
        </div>
      )}

      {/* Checkbox Concluído */}
      <div className="flex items-center gap-2 p-3 bg-slate-50 rounded">
        <input type="checkbox" id="te_completed"
          checked={teData.completed || false}
          onChange={(e) => setTeData(prev => ({ ...prev, completed: e.target.checked }))}
          className="form-checkbox h-5 w-5 text-blue-600"
        />
        <label htmlFor="te_completed" className="text-sm font-medium text-slate-700">
          Marcar sessão TE como concluída
        </label>
      </div>
    </div>
  );
});

export default TETab;
